import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import ComponentName from '../constant/ComponentName';
import FolderClient from '../client/folder/FolderClient';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import ActionClient from '../client/action/ActionClient';
import {
  ActionJsonModel,
  ApiActionJsonModel,
  DiscordActionJsonModel,
  EmailActionJsonModel,
  JiraActionJsonModel,
  MicrosoftTeamsJsonModel,
  SlackActionJsonModel,
} from '../domain/collection/rule/action/ActionDomains';
import _ from 'lodash';
import { query } from '@trlogic/client-js';
import { TransactionData } from '../domain/stream/transaction/TransactionDomains';
import { changeActionDirty } from '../redux/actions/ActionActions';
import { ActionType } from '../constant/Constants';
import { WatchlistBaseModel, WatchlistEntityRequest, WatchlistGeneralRequest, WatchlistIndividualRequest } from '../domain/watchlist/WatchlistDomains';
import { ACTION_NOT_PERMITTED_ERROR, hasCasesActionPermission } from '../util/CaseBulkCloseUtil';
import SelectQuery = query.SelectQuery;
import DeployErrorUtil from '../util/DeployErrorUtil';

class ActionService {
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  private actionClient: ActionClient = GatewayContainer.getInstance().get(GatewayNames.ACTION_CLIENT);

  public async save(_action: ActionJsonModel, isUpdate: boolean, folder: EnrichedComponentCategory) {
    const action = _.cloneDeep(_action);
    try {
      const isExist: boolean = await this.actionClient.exist(action.name);
      if (isExist && !isUpdate) {
        MessageUtil.alreadyExist(LanguageUtil.get({ id: 'common.label.action' }));
        return false;
      }
      const isValid = this.actionIsValid(action);
      if (!isValid) return false;
      await this.actionClient.save(action);
      if (!isUpdate && folder != undefined) {
        await this.folderClient.addItemToCategory(ComponentName.ACTION, folder.id, action.name);
      }
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        detail: (
          <div>
            <b>{LanguageUtil.get({ id: 'messages.success.saved', defaultMessage: `${action.name} saved` }, { 0: `${action.name}` })}</b>
          </div>
        ),
      });
      dispatch(changeActionDirty(false));
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        detail: (
          <div>
            <b>{LanguageUtil.get({ id: 'messages.error.cannotSave', defaultMessage: `Cannot save ${action.name}` }, { 0: `${action.name}` })}</b>
          </div>
        ),
      });
      console.error(e);
      return false;
    }
  }

  public async remove(action: ActionJsonModel) {
    if (action.name) {
      await this.actionClient
        .remove(action.name)
        .then((response) => {
          if (response.code) {
            return false;
          }
          MessageUtil.success({
            summary: LanguageUtil.get({ id: 'common.label.actionRemoved', defaultMessage: 'Action Removed' }),
            detail: (
              <div>
                <b>{LanguageUtil.get({ id: 'messages.success.removed', defaultMessage: `${action.name} Removed` }, { 0: `${action.name}` })}</b>
              </div>
            ),
          });
          return true;
        })
        .catch((e) => {
          if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }))) {
            return false;
          }
          const dependencies: any = e.response.data;
          if (dependencies?.message == 'Dependency Exception') {
            dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.ACTION));
          }
          MessageUtil.error({
            summary: LanguageUtil.get({ id: 'common.label.actionCannotBeRemoved', defaultMessage: 'Action can not be removed' }),
            detail: (
              <div>
                <b>{LanguageUtil.get({ id: 'messages.error.cannotSave', defaultMessage: `Cannot save ${action.name}` }, { 0: `${action.name}` })}</b>
              </div>
            ),
          });
          return false;
        });
    } else {
      return false;
    }
  }

  public async deploy(action: ActionJsonModel) {
    try {
      await this.actionClient.deploy(action.name);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.success.actionDeployed', defaultMessage: 'Action can not be removed' })}</div>,
      });
    } catch (error: unknown) {
      if (DeployErrorUtil.show(error, LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }))) {
        return;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        detail: (
          <div>
            <b></b> {LanguageUtil.get({ id: 'messages.error.cannotDeployed', defaultMessage: `can not deployed ${action.name}` }, { 0: `${action.name}` })}
          </div>
        ),
      });
    }
  }

  public count(query: SelectQuery): Promise<number> {
    return this.actionClient.count(query);
  }

  public async bulkAction(ids: string[], type: ActionType): Promise<BulkActionDependencyError[]> {
    const logs: any = await this.actionClient.bulkAction(ids, type);
    return logs;
  }

  public async undeploy(action: ActionJsonModel) {
    try {
      await this.actionClient.undeploy(action.name);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        detail: (
          <div>
            <b>{action.name}</b> {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeployed' })}
          </div>
        ),
      });
    } catch (e) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }))) {
        return;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        detail: (
          <div>
            <b> {LanguageUtil.get({ id: 'messages.cannotUndeployed', defaultMessage: `can not undeployed ${action.name}` }, { 0: `${action.name}` })}</b>
          </div>
        ),
      });
    }
  }

  private actionIsValid(action: ActionJsonModel) {
    switch (action.template) {
      case 'email':
        return this.emailChecked(action as EmailActionJsonModel);
      case 'api':
        return this.apiChecked(action as ApiActionJsonModel);
      case 'discord':
        return this.discordChecked(action as DiscordActionJsonModel);
      case 'microsoft-teams':
        return this.microsoftTeamsChecked(action as MicrosoftTeamsJsonModel);
      case 'jira':
        return this.jiraChecked(action as JiraActionJsonModel);
      case 'slack':
        return this.slackChecked(action as SlackActionJsonModel);
      default:
        return true;
    }
  }

  private emailChecked(action: EmailActionJsonModel) {
    if (
      action.name == undefined ||
      action.name.trim().length == 0 ||
      action.smtpPort == undefined ||
      action.smtpHost == undefined ||
      action.smtpHost.trim().length == 0 ||
      action.user == undefined ||
      action.user.trim().length == 0 ||
      action.password == undefined ||
      action.password.trim().length == 0 ||
      action.to == undefined ||
      action.to.trim().length == 0
    ) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),

        detail: (
          <div>{LanguageUtil.get({ id: 'messages.error.fieldsAreRequired', defaultMessage: 'SmtpHost, smtpPort, user, password to fields are required' })}</div>
        ),
      });
      return false;
    }
    return true;
  }

  private apiChecked(action: ApiActionJsonModel) {
    if (
      action.name == undefined ||
      action.name.trim().length == 0 ||
      action.url == undefined ||
      action.url.trim().length == 0 ||
      action.method == undefined ||
      action.method.trim().length == 0
    ) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),

        detail: <div>{LanguageUtil.get({ id: 'messages.error.urlMethodAreRequired', defaultMessage: 'Url, method fields are required' })}</div>,
      });
      return false;
    }
    return true;
  }

  private jiraChecked(action: JiraActionJsonModel) {
    if (
      action.name == undefined ||
      action.name.trim().length == 0 ||
      action.tenant == undefined ||
      action.tenant.trim().length == 0 ||
      action.username == undefined ||
      action.username.trim().length == 0 ||
      action.password == undefined ||
      action.password.trim().length == 0 ||
      action.projectKey == undefined ||
      action.projectKey.trim().length == 0
    ) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),

        detail: (
          <div>{LanguageUtil.get({ id: 'messages.error.tenantRequired', defaultMessage: 'Tenant, username, password, projectKey fields are required' })}</div>
        ),
      });
      return false;
    }
    return true;
  }

  private discordChecked(action: DiscordActionJsonModel) {
    if (
      action.name == undefined ||
      action.name.trim().length == 0 ||
      action.channel == undefined ||
      action.channel.trim().length == 0 ||
      action.token == undefined ||
      action.token.trim().length == 0 ||
      action.message == undefined ||
      action.message.trim().length == 0
    ) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),

        detail: <div>{LanguageUtil.get({ id: 'messages.error.channelRequired', defaultMessage: 'Channel, token, message fields are required' })}</div>,
      });
      return false;
    }
    return true;
  }

  private microsoftTeamsChecked(action: MicrosoftTeamsJsonModel) {
    if (
      action.name == undefined ||
      action.name.trim().length == 0 ||
      action.tenant == undefined ||
      action.tenant.trim().length == 0 ||
      action.channel == undefined ||
      action.channel.trim().length == 0 ||
      action.token == undefined ||
      action.token.trim().length == 0 ||
      action.message == undefined ||
      action.message.trim().length == 0
    ) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),

        detail: <div>{LanguageUtil.get({ id: 'messages.error.channelRequired', defaultMessage: 'Channel, token, message fields are required' })}</div>,
      });
      return false;
    }
    return true;
  }

  private slackChecked(action: SlackActionJsonModel) {
    if (
      action.name == undefined ||
      action.name.trim().length == 0 ||
      action.token == undefined ||
      action.token.trim().length == 0 ||
      action.channel == undefined ||
      action.channel.trim().length == 0 ||
      action.message == undefined ||
      action.message.trim().length == 0
    ) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),

        detail: <div>{LanguageUtil.get({ id: 'messages.error.channelRequired', defaultMessage: 'Channel, token, message fields are required' })}</div>,
      });
      return false;
    }
    return true;
  }

  public sendAction(action: string, transaction: TransactionData) {
    if (!hasCasesActionPermission()) {
      throw new Error(ACTION_NOT_PERMITTED_ERROR);
    }
    try {
      return this.actionClient.sendAction(action, transaction);
    } catch (e) {
      console.error(e);
    }
  }

  public sendKycAction(action: string, request: WatchlistIndividualRequest | WatchlistEntityRequest | WatchlistGeneralRequest, response: WatchlistBaseModel[]) {
    try {
      return this.actionClient.sendKycAction(action, request, response);
    } catch (e) {
      console.error(e);
    }
  }
}

export default ActionService;
